import { useState } from "react";
import { ArrowRight, MapPin, CalendarDays, CloudSun } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useEnquiry } from "@/components/Enquiry";
import { ITINERARIES } from "@/data/destinations";
import { img } from "@/data/images";

const LENGTHS = ["1 day", "2 days", "3 days", "5 days", "7 days", "Custom"];

export default function Itineraries() {
  const { openEnquiry } = useEnquiry();
  const [picked, setPicked] = useState({});

  return (
    <>
      <Seo
        title="Uttarakhand Weekend Trips & Family Itineraries — Taxi Enquiry | Tingadya Travels"
        description="Ready-to-enquire Uttarakhand itineraries — Dehradun–Mussoorie, Haridwar–Rishikesh, Nainital lakes, Auli, Chopta and more — with 1 to 7 day and customised taxi trip options."
      />
      <section className="relative overflow-hidden pb-8 pt-28 sm:pt-36" data-testid="itineraries-hero">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(212,175,55,0.14),transparent)]" />
        <div className="container-x relative z-10">
          <p className="eyebrow">Ready-to-Enquire Plans</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl">Family Holidays & Weekend Trips</h1>
          <p className="mt-4 max-w-2xl leading-relaxed text-mist">
            Ten trusted routes, shaped around your days. Pick a trip length, tell us your dates, and we confirm road conditions and season notes before the plan is final.
          </p>
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/10 p-5" data-testid="itineraries-season-caveat">
            <CloudSun size={18} className="mt-0.5 shrink-0 text-gold" />
            <p className="text-sm leading-relaxed text-snow/90">
              Every itinerary is a starting point, not a promise — road conditions, weather, travel duration and seasonal openings change through the year. We reconfirm the plan with you before departure.
            </p>
          </div>
        </div>
      </section>

      <section className="section-pad pt-10" data-testid="itineraries-list">
        <div className="container-x grid gap-8 md:grid-cols-2">
          {ITINERARIES.map((it, i) => {
            const length = picked[it.slug] || "2 days";
            return (
              <Reveal key={it.slug} delay={(i % 2) * 0.07}>
                <article className="flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-coal card-lift" data-testid={`itinerary-card-${it.slug}`}>
                  <div className="relative aspect-[16/8] overflow-hidden">
                    <img src={img(it.images[0], 1000)} alt={`${it.name} — Uttarakhand weekend trip itinerary`} loading="lazy" className="img-zoom h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <span className="absolute left-4 top-4 rounded-full bg-black/60 px-3 py-1 text-[11px] font-semibold tracking-wide text-gold backdrop-blur-md border border-gold/30">
                      {it.region} Circuit
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h2 className="font-display text-2xl font-bold text-white">{it.name}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-mist">{it.description}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {it.stops.map((s) => (
                        <span key={s} className="chip" data-testid={`itinerary-stop-${it.slug}`}><MapPin size={11} className="text-gold" /> {s}</span>
                      ))}
                    </div>
                    <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-slategray">
                      <CloudSun size={13} className="mt-0.5 shrink-0 text-gold/70" /> {it.season}
                    </p>
                    <div className="mt-5">
                      <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-mist"><CalendarDays size={12} className="text-gold" /> Trip length</p>
                      <div className="flex flex-wrap gap-2">
                        {LENGTHS.map((l) => (
                          <button
                            key={l}
                            onClick={() => setPicked((p) => ({ ...p, [it.slug]: l }))}
                            data-testid={`itinerary-length-${it.slug}-${l.replace(/\s/g, "")}`}
                            className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${length === l ? "border-gold bg-gold text-ink" : "border-white/15 bg-white/5 text-mist hover:border-gold/50"}`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => openEnquiry(it.name, `Itineraries page: ${it.slug}`, { tripDays: length === "Custom" ? "Customised trip" : length })}
                      data-testid={`itinerary-enquire-${it.slug}`}
                      className="btn-gold mt-6 w-full"
                    >
                      Enquire for {length === "Custom" ? "a customised trip" : length} <ArrowRight size={15} />
                    </button>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>
    </>
  );
}
